import {
  Box,
  Typography,
  Stack,
  Chip,
  useTheme,
  Radio,
  Divider,
} from "@mui/material";
import { LocalShipping, Speed, ExpandMore } from "@mui/icons-material";
import { useState } from "react";
import { handleSwitchKeyDown } from "../../../../utils/numberInputValidation";
import type { RateQuoteReviewPricingDetails } from "./rateQuoteReviewPricing.type";
import {
  chargeRowStyles,
  RATE_QUOTE_SERVICE_CARD_HEADER_HEIGHT_PX,
  RATE_QUOTE_SERVICE_CARD_MAX_WIDTH_PX,
  RATE_QUOTE_SERVICE_CARD_QUOTE_NUMBER_FONT_SIZE_PX,
  rateQuoteReviewRecommendedChipStyles,
  rateQuoteReviewChargeLabelStyles,
  rateQuoteReviewChargeValueStyles,
  serviceIconStyles,
} from "./RateQuoteServiceOptionCard.styles";

type RateQuoteServiceOptionType = "standard" | "guaranteed" | "volume";

interface RateQuoteServiceOptionCardProps {
  serviceType: RateQuoteServiceOptionType;
  quoteDetails: RateQuoteReviewPricingDetails;
  isSelected?: boolean;
  onSelect?: (id: string) => void;
  defaultQuoteDetailsExpanded?: boolean;
  serviceDisplayName: string;
  quoteNumberDisplay?: string;
  quoteNumberPrefix?: string;
  collapsedPriceAddon?: string;
  guaranteedServiceChargeDisplay?: string;
  includeGuaranteedServiceLine?: boolean;
  showRecommendedBadge?: boolean;
  showSpeedIcon?: boolean;
  showTruckIcon?: boolean;
}

const formatUsdCharge = (amount: string) => `$${amount}`;

const formatDiscountLine = (amount: string, percent: string) =>
  `-$${amount} (${percent}%)`;

export const RateQuoteServiceOptionCard = ({
  serviceType,
  quoteDetails,
  isSelected = false,
  onSelect,
  defaultQuoteDetailsExpanded = false,
  serviceDisplayName,
  quoteNumberDisplay,
  quoteNumberPrefix = "",
  collapsedPriceAddon,
  guaranteedServiceChargeDisplay,
  includeGuaranteedServiceLine = false,
  showRecommendedBadge = false,
  showSpeedIcon = false,
  showTruckIcon = false,
}: RateQuoteServiceOptionCardProps) => {
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(defaultQuoteDetailsExpanded);

  const serviceInfo =
    serviceType === "volume"
      ? {
          quoteId: quoteDetails.volume.quoteId,
          name: serviceDisplayName,
          totalAmount: quoteDetails.volume.totalAmount,
          guaranteedServiceCharge: includeGuaranteedServiceLine
            ? quoteDetails.volume.guaranteedService
            : undefined,
        }
      : serviceType === "standard"
        ? {
            quoteId: quoteDetails.standard.quoteId,
            name: serviceDisplayName,
            totalAmount: quoteDetails.standard.totalAmount,
            guaranteedServiceCharge: undefined,
          }
        : {
            quoteId: quoteDetails.guaranteed.quoteId,
            name: serviceDisplayName,
            totalAmount: quoteDetails.guaranteed.totalAmount,
            guaranteedServiceCharge: includeGuaranteedServiceLine
              ? quoteDetails.guaranteed.guaranteedService
              : undefined,
          };

  const quoteNumber =
    quoteNumberDisplay ??
    (serviceType === "standard"
      ? quoteDetails.standard.quoteId
      : serviceType === "volume"
        ? quoteDetails.volume.quoteId
        : quoteDetails.guaranteed.quoteId);

  const additionalServices = quoteDetails.additionalServices || [];

  const renderChargeRow = (label: string, value: string) => (
    <Box
      sx={chargeRowStyles}
      key={label}
      role="listitem"
      aria-label={`${label} ${value}`}
    >
      <Typography variant="body2" sx={rateQuoteReviewChargeLabelStyles}>
        {label}
      </Typography>
      <Typography variant="body2" sx={rateQuoteReviewChargeValueStyles}>
        {value}
      </Typography>
    </Box>
  );

  const serviceCardAriaLabel = [
    serviceInfo.name,
    `${quoteNumberPrefix}${quoteNumber}`,
    showRecommendedBadge ? "RECOMMENDED" : undefined,
    collapsedPriceAddon,
    `$${serviceInfo.totalAmount}`,
  ]
    .filter((part): part is string => Boolean(part))
    .join(", ");

  return (
    <Box
      role="group"
      aria-label={serviceCardAriaLabel}
      sx={{
        width: "100%",
        maxWidth: `${RATE_QUOTE_SERVICE_CARD_MAX_WIDTH_PX}px`,
        flex: "1 1 0",
        minWidth: 0,
        height: "fit-content",
        border: "1px solid",
        borderColor: isSelected ? theme.palette.primary.main : "divider",
        borderRadius: "8px",
        position: "relative",
        bgcolor: "background.paper",
        display: "flex",
        flexDirection: "column",
        cursor: "pointer",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
      tabIndex={0}
      onClick={() => onSelect?.(serviceInfo.quoteId)}
      onKeyDown={(e) =>
        handleSwitchKeyDown(e, false, () => {
          onSelect?.(serviceInfo.quoteId);
        })
      }
    >
      <Box sx={{ pb: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            height: `${RATE_QUOTE_SERVICE_CARD_HEADER_HEIGHT_PX}px`,
            minHeight: `${RATE_QUOTE_SERVICE_CARD_HEADER_HEIGHT_PX}px`,
            boxSizing: "border-box",
            px: 2,
            py: 0,
            gap: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flex: 1,
              minWidth: 0,
            }}
          >
            <Radio
              value={serviceInfo.quoteId}
              checked={isSelected}
              tabIndex={-1}
              inputProps={{ "aria-label": serviceInfo.name }}
              sx={{
                padding: 0,
                "& .MuiSvgIcon-root": {
                  fontSize: 24,
                },
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: theme.palette.primary.main,
                fontSize: "20px",
                lineHeight: "160%",
                letterSpacing: "0.15px",
                whiteSpace: "nowrap",
              }}
            >
              {serviceInfo.name}
            </Typography>
          </Box>

          {(showTruckIcon || showRecommendedBadge || showSpeedIcon) && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexShrink: 0,
              }}
            >
              {showTruckIcon && (
                <LocalShipping sx={{ ...serviceIconStyles(theme) }} />
              )}
              {showRecommendedBadge && (
                <Chip
                  label="RECOMMENDED"
                  aria-label="RECOMMENDED"
                  sx={rateQuoteReviewRecommendedChipStyles(theme)}
                />
              )}
              {showSpeedIcon && <Speed sx={serviceIconStyles(theme)} />}
            </Box>
          )}
        </Box>
        <Divider />
      </Box>

      <Box
        sx={{
          flex: 1,
          mx: 3,
          mt: 2,
          mb: isExpanded ? 2 : 1,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            fontSize: "16px",
            fontWeight: 400,
            lineHeight: "150%",
            letterSpacing: "0.15px",
          }}
        >
          Quote
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 0.5,
            gap: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.grey[900],
              fontSize: `${RATE_QUOTE_SERVICE_CARD_QUOTE_NUMBER_FONT_SIZE_PX}px`,
              fontWeight: 700,
              lineHeight: "160%",
              letterSpacing: "0.15px",
            }}
          >
            {quoteNumberPrefix}
            {quoteNumber}
          </Typography>
          <Box
            component="button"
            type="button"
            aria-expanded={isExpanded}
            aria-label="Quote Details"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              cursor: "pointer",
              border: "none",
              background: "none",
              padding: 0,
              color: theme.palette.primary.main,
            }}
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded((prev) => !prev);
            }}
          >
            <ExpandMore
              sx={{
                fontSize: 20,
                transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                transition: "transform 0.3s",
              }}
            />
            <Typography
              variant="body2"
              component="span"
              sx={{
                fontSize: "15px",
                fontWeight: 500,
                lineHeight: "26px",
                letterSpacing: "0.46px",
              }}
            >
              Quote Details
            </Typography>
          </Box>
        </Box>

        {isExpanded && (
          <Stack role="list" sx={{ mt: 3, gap: 0 }}>
            {renderChargeRow(
              "Gross Charges",
              formatUsdCharge(quoteDetails.grossCharge)
            )}
            {renderChargeRow(
              "Discount",
              formatDiscountLine(
                quoteDetails.discountAmount,
                quoteDetails.discountPercent
              )
            )}
            {renderChargeRow(
              "Freight Charges",
              formatUsdCharge(quoteDetails.freightCharges)
            )}
            {additionalServices.map((additionalService) =>
              renderChargeRow(
                additionalService.title,
                formatUsdCharge(additionalService.charge)
              )
            )}
            {renderChargeRow(
              "Fuel Surcharge",
              formatUsdCharge(quoteDetails.fuelSurcharge)
            )}
            {serviceInfo.guaranteedServiceCharge &&
              renderChargeRow(
                "Guaranteed Service",
                guaranteedServiceChargeDisplay ??
                  formatUsdCharge(serviceInfo.guaranteedServiceCharge)
              )}
          </Stack>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-end",
          ...(isExpanded && {
            borderTop: "1px solid",
            borderColor: "divider",
          }),
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mx: 3,
            my: 3,
          }}
        >
          {collapsedPriceAddon && (
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", fontSize: "16px", mr: 1 }}
            >
              {collapsedPriceAddon}
            </Typography>
          )}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 400,
              color: theme.palette.grey[900],
              fontSize: "34px",
              lineHeight: 1.235,
              letterSpacing: "0.25px",
            }}
          >
            ${serviceInfo.totalAmount}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
